import { useState } from "react";
import {
  useCreateAlumniMutation,
  useDeleteAlumniMutation,
  useGetAdminAlumniQuery,
  useUploadImageMutation,
} from "../../features/api/appApi";
import AdminSectionCard, {
  AdminButton,
  AdminEmptyState,
  AdminField,
  AdminFormGrid,
  AdminInput,
  AdminScrollTable,
  AdminStatusBadge,
} from "./AdminSectionCard";

const defaultAlumniForm = {
  name: "",
  designation: "",
  imageUrl: "",
  sortOrder: 0,
};

const toBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });

const AdminAlumniPage = () => {
  const { data: alumni = [] } = useGetAdminAlumniQuery();
  const [createAlumni] = useCreateAlumniMutation();
  const [deleteAlumni] = useDeleteAlumniMutation();
  const [uploadImage, { isLoading: uploadLoading }] = useUploadImageMutation();

  const [alumniForm, setAlumniForm] = useState(defaultAlumniForm);

  const onUploadAlumniImage = async (file) => {
    if (!file) return;
    const imageBase64 = await toBase64(file);
    const url = await uploadImage({ imageBase64 }).unwrap();
    if (url) {
      setAlumniForm((prev) => ({ ...prev, imageUrl: url }));
    }
  };

  return (
    <AdminSectionCard title="Alumni" subtitle="Upload image and publish alumni">
      <form
        className="space-y-3"
        onSubmit={async (e) => {
          e.preventDefault();
          await createAlumni({
            ...alumniForm,
            sortOrder: Number(alumniForm.sortOrder),
          });
          setAlumniForm(defaultAlumniForm);
        }}
      >
        <AdminFormGrid>
          <AdminField label="Name">
            <AdminInput
              placeholder="Name"
              value={alumniForm.name}
              onChange={(e) => setAlumniForm({ ...alumniForm, name: e.target.value })}
              required
            />
          </AdminField>

          <AdminField label="Designation">
            <AdminInput
              placeholder="Designation"
              value={alumniForm.designation}
              onChange={(e) => setAlumniForm({ ...alumniForm, designation: e.target.value })}
            />
          </AdminField>

          <AdminField label="Image URL">
            <AdminInput
              placeholder="Image URL"
              value={alumniForm.imageUrl}
              onChange={(e) => setAlumniForm({ ...alumniForm, imageUrl: e.target.value })}
              required
            />
          </AdminField>

          <AdminField label="Sort order">
            <AdminInput
              type="number"
              placeholder="Sort order"
              value={alumniForm.sortOrder}
              onChange={(e) => setAlumniForm({ ...alumniForm, sortOrder: e.target.value })}
            />
          </AdminField>

          <AdminField label="Upload image" className="md:col-span-2">
            <AdminInput
              className="cursor-pointer pt-2"
              type="file"
              accept="image/*"
              onChange={(e) => onUploadAlumniImage(e.target.files?.[0])}
            />
          </AdminField>
        </AdminFormGrid>

        {uploadLoading ? <AdminStatusBadge tone="blue">Uploading image...</AdminStatusBadge> : null}

        <AdminButton className="w-full md:w-auto" type="submit">
          Create Alumni
        </AdminButton>
      </form>

      <div className="mt-5">
        {alumni.length ? (
          <AdminScrollTable>
            <table className="w-full min-w-[680px] text-left text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Designation</th>
                  <th className="px-4 py-3">Sort</th>
                  <th className="px-4 py-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {alumni.map((row) => (
                  <tr key={row.id} className="bg-white hover:bg-slate-50/70">
                    <td className="max-w-[260px] break-words px-4 py-3 font-semibold text-slate-800">
                      {row.name}
                    </td>
                    <td className="max-w-[260px] break-words px-4 py-3 text-slate-600">
                      {row.designation || "-"}
                    </td>
                    <td className="px-4 py-3 text-slate-600">{row.sortOrder ?? "-"}</td>
                    <td className="px-4 py-3 text-right">
                      <AdminButton
                        variant="danger"
                        className="h-9 px-3 text-xs"
                        onClick={() => deleteAlumni(row.id)}
                      >
                        Delete
                      </AdminButton>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </AdminScrollTable>
        ) : (
          <AdminEmptyState text="No alumni records yet." />
        )}
      </div>
    </AdminSectionCard>
  );
};

export default AdminAlumniPage;
